import { DetailedHTMLProps, HTMLAttributes, ReactNode } from 'react';

import { TWidgetIframeParentDimension } from '@lib/core/service/types/interface';
import {
  ILocaleText,
  ILocaleType,
  LocaleFragment,
  TInsertIntlJSXPartOptions,
} from '@lib/tools/locale/views/LocaleFragment';

import { StyledText } from '@components/web/src/foundations/Text/Text.styles';
import {
  TFontStyle,
  TFontType,
  TStyleWeight,
  TTextAlight,
  TTextDecoration,
  TTextTransform,
  TTypographyVariants,
} from '@components/web/src/foundations/Text/types';

export interface ITextProps extends DetailedHTMLProps<HTMLAttributes<HTMLSpanElement>, HTMLSpanElement> {
  text?: ILocaleText;
  color?: string;
  shouldRenderEmptySelector?: boolean;
  size?: TTypographyVariants;
  weight?: TStyleWeight;
  disableMarkdown?: boolean;
  fontType?: TFontType;
  localeOptions?: Record<string, string>;
  localeIndex?: Record<string, string>;
  localeVariables?: Record<string, ILocaleType>;
  transformText?: 'default' | 'none' | 'lowercase' | 'markdown';
  linesLimit?: number;
  textDecoration?: TTextDecoration;
  fontStyle?: TFontStyle;
  children?: ReactNode;
  textAlign?: TTextAlight;
  textTransform?: TTextTransform;
  handleClick?: (e) => void;
  strongSize?: TTypographyVariants;
  strongColor?: string;
  strongFontStyle?: TFontStyle;
  strongWeight?: TStyleWeight;
  insertIntlJSXPartOptions?: TInsertIntlJSXPartOptions;
  dimension?: TWidgetIframeParentDimension;
}

export const Text = ({
  text,
  fontType,
  size = 'body3',
  weight = 'normal',
  color,
  strongSize = size,
  strongColor,
  strongFontStyle,
  strongWeight = 'bold',
  shouldRenderEmptySelector = true,
  localeOptions,
  localeIndex,
  localeVariables,
  transformText = 'default',
  fontStyle = 'normal',
  linesLimit,
  textDecoration = 'none',
  children,
  textAlign = 'unset',
  textTransform = 'none',
  handleClick,
  insertIntlJSXPartOptions,
  dimension,
  ...rest
}: ITextProps) => {
  if (!shouldRenderEmptySelector) return null;
  return (
    <StyledText
      $color={color}
      $dimension={dimension}
      $fontStyle={fontStyle}
      $fontType={fontType}
      $linesLimit={linesLimit}
      $size={size}
      $strongColor={strongColor}
      $strongFontStyle={strongFontStyle}
      $strongSize={strongSize}
      $strongWeight={strongWeight}
      $textAlign={textAlign}
      $textDecoration={textDecoration}
      $textTransform={textTransform}
      $weight={weight}
      tabIndex={handleClick && 0}
      onClick={handleClick && handleClick}
      onKeyUp={handleClick && handleClick}
      {...rest}
    >
      {!!text && (
        <LocaleFragment
          index={localeIndex}
          insertIntlJSXPartOptions={insertIntlJSXPartOptions}
          message={text}
          options={localeOptions}
          transformText={transformText}
          variables={localeVariables}
        />
      )}
      {children}
    </StyledText>
  );
};
